// core/stores/postStore.ts — Zustand store for the post feed and engagement state.
//
// Hydrated at module init from the typed seed (seedPosts). Services mutate this
// store; hooks read it reactively. Components must use hooks, not this store.

import { create } from 'zustand';
import type { Post } from '@/core/models/post';
import { seedPosts } from '@/core/data/posts';
import { bootSeed } from './bootSeed';
import { normHandle } from './followStore';

interface PostState {
  posts: Post[];

  // Replace the entire posts list (used by _resetForTest).
  setPosts: (posts: Post[]) => void;

  // Apply a single-post patch by id; no-op if the post is not found.
  patchPost: (id: string, patch: Partial<Post>) => void;

  // Insert a post fetched by id, or merge it if already present.
  // Deep-linking a post (an opened share link, a cold reload on /post/:id) fetches a post
  // the store has never seen, where patchPost would silently no-op and the page would render
  // "Post not found" despite a successful 200.
  upsertPost: (post: Post) => void;

  // Prepend a freshly-created post to the top of the feed.
  prependPost: (post: Post) => void;

  // Remove a post from the feed entirely (hide / not-interested). Distinct from
  // patchPost(deleted:true) — a deleted post stays in the feed, labeled; a HIDDEN
  // post disappears immediately client-side (the server also excludes it from
  // future feed fetches).
  removePost: (id: string) => void;

  // Remove every post authored by this (normalised) handle — used right after a
  // fresh mute so the muted author's posts disappear from the feed immediately,
  // matching hide's immediate-removal behavior instead of waiting for a refetch.
  removePostsByAuthorHandle: (normalizedHandle: string) => void;

  // Re-insert a post previously taken out via removePost — powers the "Undo" toast
  // action on hide. `index` restores its original position when known (best-effort;
  // omit to just put it back at the top). No-ops if the post is already present
  // (e.g. a stale double-click on Undo).
  restorePost: (post: Post, index?: number) => void;
}

export const usePostStore = create<PostState>()((set) => ({
  // Deep-clone the seed so test resets don't mutate the imported const.
  // Empty in the running app (see bootSeed.ts); the seed only exists under the test runner.
  posts: bootSeed(seedPosts).map((p) => ({ ...p })),

  setPosts: (posts) => set({ posts }),

  patchPost: (id, patch) =>
    set((s) => ({
      posts: s.posts.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    })),

  upsertPost: (post) =>
    set((s) =>
      s.posts.some((p) => p.id === post.id)
        ? { posts: s.posts.map((p) => (p.id === post.id ? { ...p, ...post } : p)) }
        : // Appended, not prepended: a deep-linked post must not jump to the top of the feed.
          { posts: [...s.posts, post] },
    ),

  prependPost: (post) => set((s) => ({ posts: [post, ...s.posts] })),

  removePost: (id) => set((s) => ({ posts: s.posts.filter((p) => p.id !== id) })),

  removePostsByAuthorHandle: (normalizedHandle) =>
    set((s) => ({
      posts: s.posts.filter((p) => normHandle(p.handle) !== normalizedHandle),
    })),

  restorePost: (post, index) =>
    set((s) => {
      if (s.posts.some((p) => p.id === post.id)) return s;
      if (index === undefined || index < 0 || index > s.posts.length) {
        return { posts: [post, ...s.posts] };
      }
      const next = [...s.posts];
      next.splice(index, 0, post);
      return { posts: next };
    }),
}));
